import { useQuery, useQueryClient } from "@tanstack/react-query";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { installConnectionIdProvider } from "@/api/client";
import { listConnections } from "@/api/connections";
import type { OneCConnectionView } from "@/types/api";
import { useAuth } from "@/auth/useAuth";

interface ConnectionContextValue {
  connections: OneCConnectionView[];
  activeConnection: OneCConnectionView | null;
  activeConnectionId: string | null;
  setActiveConnectionId: (id: string | null) => void;
  loading: boolean;
  refresh: () => void;
}

const ConnectionContext = createContext<ConnectionContextValue | null>(null);

const STORAGE_KEY = "active-connection-id";

export function ConnectionProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const connectionsQuery = useQuery({
    queryKey: ["connections"],
    queryFn: listConnections,
    enabled: !!user,
    staleTime: 60_000,
  });

  const connections = connectionsQuery.data ?? [];

  const [activeId, setActiveId] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY) ?? null,
  );

  // Register the connection ID provider for the axios interceptor.
  useEffect(() => {
    installConnectionIdProvider(() => activeId);
  }, [activeId]);

  // Auto-select: if user has exactly 1 connection and nothing selected.
  useEffect(() => {
    if (connections.length === 1 && !activeId) {
      const id = connections[0]!.id;
      setActiveId(id);
      localStorage.setItem(STORAGE_KEY, id);
    }
    // If activeId points to a deleted connection, reset.
    if (activeId && connections.length > 0) {
      const exists = connections.some((c) => c.id === activeId);
      if (!exists) {
        const fallback = connections.find((c) => c.is_default) ?? connections[0];
        const newId = fallback?.id ?? null;
        setActiveId(newId);
        if (newId) localStorage.setItem(STORAGE_KEY, newId);
        else localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, [connections, activeId]);

  const activeConnection = useMemo(
    () => connections.find((c) => c.id === activeId) ?? null,
    [connections, activeId],
  );

  const handleSetActiveId = useCallback(
    (id: string | null) => {
      setActiveId(id);
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else localStorage.removeItem(STORAGE_KEY);
      // Invalidate everything — new connection = new 1С base = different data.
      queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["connections"] });
  }, [queryClient]);

  const value = useMemo<ConnectionContextValue>(
    () => ({
      connections,
      activeConnection,
      activeConnectionId: activeId,
      setActiveConnectionId: handleSetActiveId,
      loading: connectionsQuery.isLoading,
      refresh,
    }),
    [connections, activeConnection, activeId, handleSetActiveId, connectionsQuery.isLoading, refresh],
  );

  return (
    <ConnectionContext.Provider value={value}>
      {children}
    </ConnectionContext.Provider>
  );
}

export function useConnection(): ConnectionContextValue {
  const ctx = useContext(ConnectionContext);
  if (!ctx) throw new Error("useConnection must be used within ConnectionProvider");
  return ctx;
}
